import type { FilterEnumValue, FilterState } from '@parcel/filters';
import type { ConnectionCategory } from '@parcel/tools';
import type { FilterBarSchema } from '../components/filter-bar/filter-bar.js';
import type { ConnectionCatalogEntrySummary } from './spec-connections.js';

/**
 * The connectors surface's own filter schema, plus the in-memory matcher behind it. The four
 * registry entities push their FilterState to the API; the catalog is seven static rows the
 * client already holds, so it filters locally instead.
 */

/**
 * Exhaustive by construction: tsc rejects this the moment CONNECTION_CATEGORIES grows. Written
 * in the taxonomy's own order, which CONNECTOR_CATEGORY_ORDER below then reads back --
 * @parcel/tools is a devDependency of this app, so no shipped module may import its runtime
 * value. connector-filters.test.ts pins both the membership and the order against the real one.
 */
export const CONNECTOR_CATEGORY_LABELS: Readonly<Record<ConnectionCategory, string>> = {
  developer_tools_devops: 'Developer Tools & DevOps',
  collaboration_communication: 'Collaboration & Communication',
  ai_machine_learning: 'AI & Machine Learning',
  document_file_management: 'Document & File Management',
  productivity_project_management: 'Productivity & Project Management',
  crm: 'CRM',
  analytics_data: 'Analytics & Data',
  entertainment_media: 'Entertainment & Media',
  education_lms: 'Education & LMS',
  design_creative_tools: 'Design & Creative Tools',
  marketing_social_media: 'Marketing & Social Media',
  scheduling_booking: 'Scheduling & Booking',
  ecommerce: 'E-commerce',
  finance_accounting: 'Finance & Accounting',
  sales_customer_support: 'Sales & Customer Support',
  hr_recruiting: 'HR & Recruiting',
  social_media: 'Social Media',
  workflow_automation: 'Workflow Automation',
  data_analytics: 'Data & Analytics',
  advertising_marketing: 'Advertising & Marketing',
};

const CONNECTOR_CATEGORY_ORDER = Object.keys(CONNECTOR_CATEGORY_LABELS) as ConnectionCategory[];

export const CONNECTOR_CONNECTED_FIELD = 'connected';

const CONNECTED_VALUE = 'connected';
const NOT_CONNECTED_VALUE = 'not_connected';

/**
 * The connectors page's three fixed views, expressed the way every other surface expresses a
 * view: as a preset FilterState, not as parallel state beside one. Picking the Connected tab
 * and picking Connected in the filter bar are the same action, so the tab and the chip can
 * never disagree. Not saved views -- there is nothing here worth persisting per member, so the
 * page passes no `actions` to ViewTabs and the Views menu never renders.
 */
export const CONNECTOR_VIEWS = [
  { id: CONNECTED_VALUE, name: 'Connected' },
  { id: NOT_CONNECTED_VALUE, name: 'Not connected' },
] as const;

export type ConnectorViewId = (typeof CONNECTOR_VIEWS)[number]['id'];

export function isConnectorViewId(value: string): value is ConnectorViewId {
  return CONNECTOR_VIEWS.some((view) => view.id === value);
}

/** The tab a state lights up, or null for the base All tab. */
export function activeConnectorViewId(state: FilterState): ConnectorViewId | null {
  const selected = state.fields[CONNECTOR_CONNECTED_FIELD];
  if (!Array.isArray(selected) || selected.length !== 1) return null;
  const [value] = selected;
  return value === CONNECTED_VALUE || value === NOT_CONNECTED_VALUE ? value : null;
}

/** Switching tabs sets one field and leaves every other filter the member set alone. */
export function applyConnectorView(state: FilterState, view: ConnectorViewId | null): FilterState {
  const fields = { ...state.fields };
  if (view === null) delete fields[CONNECTOR_CONNECTED_FIELD];
  else fields[CONNECTOR_CONNECTED_FIELD] = [view];
  return { ...state, fields };
}

export const CONNECTOR_CATEGORY_FIELD = 'category';

/**
 * dynamic-enum, not enum: the options are the categories the loaded catalog actually uses,
 * so the menu never offers one that matches nothing. `derived-rows` is the value source the
 * filters package reserves for exactly this (see FilterValueSource).
 */
export const connectorFilterSchema: FilterBarSchema = {
  entity: 'connector',
  fields: [
    {
      key: CONNECTOR_CONNECTED_FIELD,
      label: 'Status',
      description: 'Whether this connector is already connected for the scope you are viewing.',
      // Single-select: the two values partition the catalog, so picking both means nothing.
      type: 'enum',
      multi: false,
      values: [
        { value: CONNECTED_VALUE, label: 'Connected' },
        { value: NOT_CONNECTED_VALUE, label: 'Not connected' },
      ],
    },
    {
      key: CONNECTOR_CATEGORY_FIELD,
      label: 'Category',
      description: 'What a connector is for, using the provider catalog’s own taxonomy.',
      type: 'dynamic-enum',
      multi: true,
      valueSource: 'derived-rows',
    },
  ],
};

/** Catalog order, deduped: the taxonomy's declaration order, not whatever the API returned. */
export function connectorCategoryOptions(
  entries: readonly ConnectionCatalogEntrySummary[],
): FilterEnumValue[] {
  const present = new Set<string>();
  for (const entry of entries) for (const category of entry.categories) present.add(category);
  return CONNECTOR_CATEGORY_ORDER.filter((category) => present.has(category)).map((category) => ({
    value: category,
    label: CONNECTOR_CATEGORY_LABELS[category],
  }));
}

function matchesQuery(entry: ConnectionCatalogEntrySummary, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (
    entry.name.toLowerCase().includes(needle) ||
    entry.description.toLowerCase().includes(needle) ||
    entry.categories.some((category) =>
      CONNECTOR_CATEGORY_LABELS[category].toLowerCase().includes(needle),
    )
  );
}

/**
 * AND across fields, OR within one: the same shape the API-backed filters use, so a member
 * who learns the bar on accounts reads the same result here. A field this schema does not
 * declare is ignored rather than treated as "matches nothing", so a stale saved state cannot
 * blank the page.
 */
export function matchesConnectorFilters(
  entry: ConnectionCatalogEntrySummary,
  state: FilterState,
  scope: { isConnected: boolean },
): boolean {
  if (!matchesQuery(entry, state.q)) return false;

  const view = activeConnectorViewId(state);
  if (view !== null && !(view === CONNECTED_VALUE ? scope.isConnected : !scope.isConnected)) {
    return false;
  }

  const categories = state.fields[CONNECTOR_CATEGORY_FIELD];
  if (!Array.isArray(categories) || categories.length === 0) return true;
  return categories.some((category) => entry.categories.includes(category as ConnectionCategory));
}
